import { Component, computed, DestroyRef, inject, LOCALE_ID, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InvoiceFilters } from './invoice-filters/invoice-filters';
import { InvoiceTable, LoadState } from './invoice-table/invoice-table';
import { Invoices } from '../../../pages/admin-dashboard/admin-invoice/admin-invoice.model';
import { InvoiceService } from '../../../services/invoices/invoices.service';
import { PharmacyAutoCompleteApiItem } from '../../../services/pharmace-auto-complete/pharmace-auto-complete.service';
import { InvoiceFiltersModel } from './invoice-filters/Invoice-filters.model';
import { PharmacyService } from '../../../services/pharmacy/pharmacy.service';
import { LookupVTO } from '../../../pages/admin-dashboard/admin-companies/admin-companies.model';

const INVOICE_STATUS_DOMAIN = 5901;

@Component({
  selector: 'app-invoice-list-component',
  standalone: true,
  imports: [InvoiceFilters, InvoiceTable],
  templateUrl: './invoice-list-component.html',
  styleUrl: './invoice-list-component.scss',
})
export class InvoiceListComponent implements OnInit {
  private invoiceService = inject(InvoiceService);
  private pharmacyService = inject(PharmacyService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);

  private loadInvoices$ = new Subject<void>();

  state = signal<LoadState>('loading');
  errorMessage = signal<string | null>(null);
  invoices = signal<Invoices[] | null>(null);
  currentPage = signal<number>(1);
  itemsPerPage = signal<number>(25);
  totalItems = signal<number>(0);
  isFiltersApplied = signal<boolean>(false);

  invoiceStatuses = signal<LookupVTO[]>([]);

  statusOptionsForFilter = computed(() =>
    this.invoiceStatuses().map((s) => ({
      id: s.id.toString(),
      titleEn: s.titleEn,
      titleAr: s.titleAr ?? '',
    })),
  );

  filters = signal<InvoiceFiltersModel>({
    orderBy: 'INVOICE_DATE',
    orderDir: 'DESC',
  });

  ngOnInit(): void {
    this.loadInvoiceStatuses();

    this.loadInvoices$
      .pipe(
        switchMap(() => {
          this.state.set('loading');
          this.errorMessage.set(null);

          const f = this.filters();

          return this.invoiceService.getInvoicesByFilters(
            f.invoiceId,
            f.orderId,
            f.pharmacyId,
            undefined,
            f.requestStatusId,
            f.invoiceFrom,
            f.invoiceTo,
            f.orderBy,
            f.orderDir,
            this.currentPage() - 1,
            this.itemsPerPage(),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.invoices.set(res.list ?? []);
          this.totalItems.set(res.total ?? 0);
          this.state.set('success');
        },
        error: (error) => {
          const message = this.isArabic() ? error?.error?.messageAr : error?.error?.messageEn;

          this.errorMessage.set(
            message || (this.isArabic() ? 'حدث خطأ أثناء تحميل الفواتير' : 'An error occurred while loading invoices'),
          );

          this.invoices.set([]);
          this.totalItems.set(0);
          this.state.set('error');
        },
      });

    this.loadInvoices();
  }

  loadInvoiceStatuses() {
    this.pharmacyService
      .getAllStatuses(INVOICE_STATUS_DOMAIN)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.invoiceStatuses.set(res.list ?? []),
        error: (err) => console.error('Failed to load invoice statuses', err),
      });
  }

  loadInvoices() {
    this.loadInvoices$.next();
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalItems() / this.itemsPerPage()));
  }

  toggleSortOrder() {
    this.filters.update((f) => ({
      ...f,
      orderDir: f.orderDir === 'ASC' ? 'DESC' : 'ASC',
    }));

    this.loadInvoices();
  }

  applyFilters(applied: InvoiceFiltersModel) {
    this.currentPage.set(1);
    this.isFiltersApplied.set(true);

    this.filters.update((f) => ({
      ...f,
      ...applied,
    }));

    this.loadInvoices();
  }

  resetFilters() {
    this.filters.set({
      orderBy: 'INVOICE_DATE',
      orderDir: 'DESC',
    });

    this.isFiltersApplied.set(false);
    this.currentPage.set(1);

    this.loadInvoices();
  }

  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage.set(page);
      this.loadInvoices();
    }
  }

  onPageSizeChange(size: number) {
    this.itemsPerPage.set(size);
    this.currentPage.set(1);
    this.loadInvoices();
  }

  selectPharmacy(item: PharmacyAutoCompleteApiItem) {
    this.filters.update((f) => ({
      ...f,
      pharmacyId: Number(item.id),
    }));

    this.currentPage.set(1);
    this.loadInvoices();
  }

  navigateToDetails(id: number) {
    this.router.navigate(['invoice-details', id], {
      relativeTo: this.route,
    });
  }

  isArabic() {
    return this.locale.startsWith('ar');
  }
}
